/**
 * Microsoft Authentication Library (MSAL) configuration for React application
 * Integrates with Azure AD app registration for Entra ID authentication
 */

import { Configuration, LogLevel } from '@azure/msal-browser';

// Get environment variables from Vite (replaced at build time)
const tenantId = import.meta.env.VITE_AZURE_TENANT_ID || '';
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID || '';
const baseAuthority = import.meta.env.VITE_AZURE_AUTHORITY || 'https://login.microsoftonline.com';
const apiScope = import.meta.env.VITE_AZURE_API_SCOPE || 'api://MedMgmtAuthAI-api.dev/User.MedMgmtAuthAi.API';

// Construct the tenant-specific authority URL
const authority = tenantId ? `${baseAuthority}/${tenantId}` : `${baseAuthority}/common`;
const redirectUri = window.location.origin;

/**
 * MSAL Configuration object
 * These values are configured via Azure infrastructure deployment
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId,
    authority: authority,
    redirectUri: redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage', // Use localStorage for better SSO across tabs
    storeAuthStateInCookie: false, // Prevent CSRF attacks
    secureCookies: window.location.protocol === 'https:', // Use secure cookies in production
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return; // Never log PII
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL] Auth Error:', message);
            return;
          case LogLevel.Info:
            console.info('[MSAL] Auth Info:', message);
            return;
          case LogLevel.Verbose:
            console.debug('[MSAL] Auth Debug:', message);
            return;
          case LogLevel.Warning:
            console.warn('[MSAL] Auth Warning:', message);
            return;
          default:
            return;
        }
      },
      piiLoggingEnabled: false, // Never enable PII logging
    },
    windowHashTimeout: 60000, // 1 minute timeout
    iframeHashTimeout: 20000, // Increased from 6 to 20 seconds for better reliability
    loadFrameTimeout: 0, // Use default
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * Including OpenID Connect scopes to ensure we get an ID token with roles.
 * Using custom API scope for Medical Management AI functionality.
 * The scope should match the format from infrastructure: api://MedMgmtAuthAI-api.{env}/User.MedMgmtAuthAi.API
 * Microsoft Graph scopes must be fully qualified with https://graph.microsoft.com/
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.ReadBasic.All', apiScope]
};

/**
 * Scopes for API calls - only include the scopes needed for the backend API
 * Do not include OpenID Connect scopes here as they create ID tokens instead of access tokens
 */
export const tokenRequest = {
  scopes: [apiScope], // Only the custom API scope for backend calls
  forceRefresh: false,
};

/**
 * Scopes for Microsoft Graph API calls (user profile, etc.)
 */
export const graphTokenRequest = {
  scopes: ['User.ReadBasic.All'],
  forceRefresh: false,
};

/**
 * Configuration for protecting API calls
 * Add your backend API scopes here when implementing API protection
 */
export const apiConfig = {
  backendApiScopes: [], // Add backend API scopes here when ready
};

/**
 * Configuration for Microsoft Graph API calls
 * Used specifically for accessing user profile information including photos
 * Microsoft Graph scopes must be fully qualified with https://graph.microsoft.com/
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphPhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
  graphScopes: ['User.ReadBasic.All']
};

// Debug configuration in development
if (import.meta.env.DEV) {
  console.log('MSAL Configuration:', {
    tenantId: tenantId ? `${tenantId.substring(0, 8)}...` : 'Not set',
    clientId: clientId ? `${clientId.substring(0, 8)}...` : 'Not set',
    authority: authority,
    redirectUri: redirectUri,
    apiScope: apiScope,
    envVars: {
      VITE_AZURE_TENANT_ID: import.meta.env.VITE_AZURE_TENANT_ID ? 'SET' : 'NOT_SET',
      VITE_AZURE_CLIENT_ID: import.meta.env.VITE_AZURE_CLIENT_ID ? 'SET' : 'NOT_SET',
      VITE_AZURE_AUTHORITY: import.meta.env.VITE_AZURE_AUTHORITY ? 'SET' : 'NOT_SET',
      VITE_AZURE_API_SCOPE: import.meta.env.VITE_AZURE_API_SCOPE ? 'SET' : 'NOT_SET'
    }
  });
}
